import { useState } from 'react'
import { useNegotiations, QUOTE_STATUS_CONFIG } from '../../api/sales'
import { Link } from 'react-router-dom'
import { MessageSquare, ChevronRight } from 'lucide-react'

type NegotiationFilter = 'all' | 'negotiating' | 'confirmed' | 'rejected'

const filterOptions: Array<{ value: NegotiationFilter; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'negotiating', label: '洽谈中' },
  { value: 'confirmed', label: '已成交' },
  { value: 'rejected', label: '未成交' },
]

export default function NegotiationListPage() {
  const [filter, setFilter] = useState<NegotiationFilter>('all')
  const [page, setPage] = useState(0)
  const pageSize = 20

  const { data, isLoading } = useNegotiations(filter, page, pageSize)

  const handleFilterChange = (newFilter: NegotiationFilter) => {
    setFilter(newFilter)
    setPage(0)
  }

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      negotiating: '洽谈中',
      awaiting_confirm: '待确认',
      confirmed: '已成交',
      rejected: '未成交',
      cancelled: '已作废',
      draft: '草稿',
    }
    return statusMap[status] || status
  }

  return (
    <div className="p-6">
      {/* 页面头部 */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">洽谈记录</h1>
        <p className="text-sm text-gray-500 mt-1">查看和管理所有商务洽谈记录</p>
      </div>

      {/* 筛选栏 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">状态筛选：</span>
          {filterOptions.map((fo) => (
            <button
              key={fo.value}
              onClick={() => handleFilterChange(fo.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === fo.value
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {fo.label}
            </button>
          ))}
        </div>
      </div>

      {/* 洽谈记录列表 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">报价单号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">工单号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">医院</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">设备</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">报价金额</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">最新消息</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">时间</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    加载中...
                  </td>
                </tr>
              ) : data?.content?.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <MessageSquare className="w-12 h-12 text-gray-300" />
                      <span>暂无洽谈记录</span>
                    </div>
                  </td>
                </tr>
              ) : (
                data?.content?.map((record) => (
                  <tr key={record.quotationId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/sales/negotiations/${record.quotationId}`}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        {record.quoteNo}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.workOrderNo}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.hospitalName}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{record.deviceName}</td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
                        style={{
                          color: QUOTE_STATUS_CONFIG[record.status as keyof typeof QUOTE_STATUS_CONFIG]?.color,
                          backgroundColor: QUOTE_STATUS_CONFIG[record.status as keyof typeof QUOTE_STATUS_CONFIG]?.bgColor,
                        }}
                      >
                        {getStatusLabel(record.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      ¥{record.totalAmount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      {record.latestMessage ? (
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-3 h-3 text-blue-500" />
                          <span className="text-sm text-gray-600 max-w-xs truncate">
                            {record.latestMessage}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {record.latestMessageAt
                        ? new Date(record.latestMessageAt).toLocaleDateString('zh-CN')
                        : new Date(record.updatedAt).toLocaleDateString('zh-CN')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/sales/negotiations/${record.quotationId}`}
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                        >
                          查看详情
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              共 {data.totalElements} 条记录
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                上一页
              </button>
              <span className="text-sm text-gray-600">
                第 {page + 1} / {data.totalPages} 页
              </span>
              <button
                onClick={() => setPage((p) => Math.min(p + 1, data.totalPages - 1))}
                disabled={page >= data.totalPages - 1}
                className="px-3 py-1 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
